/**
 * 实时监控页面
 *
 * 主要功能：
 * 1. 高德地图显示目标位置
 * 2. FLV 视频播放器
 * 3. 设备状态面板
 * 4. 云台控制盘
 */

import { useEffect, useLayoutEffect, useState, useCallback, useMemo, useRef } from 'react'
import { createPortal } from 'react-dom'
import { Row, Col, Card, Select, Space, Typography, message, Tag, Button } from 'antd'
import { ReloadOutlined, AimOutlined } from '@ant-design/icons'
import MapContainer from '@/components/MapContainer/Map'
import JessibucaPlayer from '@/components/VideoPlayer/JessibucaPlayer'
import VideoInteractionModal from '@/components/VideoPlayer/VideoInteractionModal'
import StatusPanel from '@/components/Dashboard/StatusPanel'
import PtzController from '@/components/Dashboard/PtzController'
import useWebSocket from '@/hooks/useWebSocket'
import { useDeviceStore, useUserStore } from '@/store/useDeviceStore'
import { get } from '@/utils/request'

const { Text } = Typography

const DEFAULT_DEVICE_OFFLINE_TIMEOUT_MS = 2000
const configuredOfflineTimeoutSeconds = Number(import.meta.env.VITE_DEVICE_OFFLINE_TIMEOUT_SECONDS)
const DEVICE_OFFLINE_TIMEOUT_MS =
  Number.isFinite(configuredOfflineTimeoutSeconds) && configuredOfflineTimeoutSeconds > 0
    ? configuredOfflineTimeoutSeconds * 1000
    : DEFAULT_DEVICE_OFFLINE_TIMEOUT_MS

// 设备信息接口（后端返回的格式）
interface DeviceFromAPI {
  dev_id: string
  name?: string
  status: 'online' | 'offline'
  last_seen?: string
  recording?: {
    vis: boolean
    ir: boolean
  }
  streams?: {
    vis?: string
    ir?: string
  }
}

function Monitor() {
  const {
    devices,
    selectedDeviceId,
    telemetryMap,
    telemetryReceivedAtMap,
    selectDevice,
    setDevices,
    updateTelemetry,
  } = useDeviceStore()
  const isAdmin = useUserStore((state) => state.isAdmin)
  const { subscribe } = useWebSocket()
  const [loading, setLoading] = useState(false)
  const [lastUdpTime, setLastUdpTime] = useState<number>(0)
  const [, setLastSeiTime] = useState<number>(0)

  // 视频刷新状态
  const [visVideoKey, setVisVideoKey] = useState(0)
  const [irVideoKey, setIrVideoKey] = useState(0)
  const [visRefreshing, setVisRefreshing] = useState(false)
  const [irRefreshing, setIrRefreshing] = useState(false)

  // 检测控制Modal状态
  const [visModalVisible, setVisModalVisible] = useState(false)
  const [irModalVisible, setIrModalVisible] = useState(false)
  const [statusCheckTime, setStatusCheckTime] = useState(() => Date.now())

  // 可搬运的播放器宿主（保持播放器实例稳定，避免Modal打开时重新拉流）
  const visPlayerHostRef = useRef<HTMLDivElement | null>(null)
  const irPlayerHostRef = useRef<HTMLDivElement | null>(null)
  if (!visPlayerHostRef.current) {
    visPlayerHostRef.current = document.createElement('div')
    visPlayerHostRef.current.style.width = '100%'
    visPlayerHostRef.current.style.height = '100%'
  }
  if (!irPlayerHostRef.current) {
    irPlayerHostRef.current = document.createElement('div')
    irPlayerHostRef.current.style.width = '100%'
    irPlayerHostRef.current.style.height = '100%'
  }

  // Monitor 卡片内的挂载位置
  const visCardSlotRef = useRef<HTMLDivElement>(null)
  const irCardSlotRef = useRef<HTMLDivElement>(null)

  // 获取设备列表
  const fetchDevices = async () => {
    setLoading(true)
    try {
      const result = await get<{ code: number; data: DeviceFromAPI[] }>(
        '/api/v1/devices/list',
        { showError: false }
      )
      if (result.code === 0 && result.data) {
        // 转换流地址格式
        const devicesWithStreams = result.data.map((d: DeviceFromAPI) => ({
          dev_id: d.dev_id,
          name: d.name,
          status: d.status,
          last_seen: d.last_seen,
          recording: {
            vis: !!d.recording?.vis,
            ir: !!d.recording?.ir,
          },
          streams: {
            vis: d.streams?.vis || '',
            ir: d.streams?.ir || '',
          },
        }))
        setDevices(devicesWithStreams)
      }
    } catch (error) {
      console.error('获取设备列表失败:', error)
      message.error('获取设备列表失败')
    } finally {
      setLoading(false)
    }
  }

  // 初始加载设备列表
  useEffect(() => {
    fetchDevices()
    // 每30秒刷新一次设备列表
    const interval = setInterval(fetchDevices, 30000)
    return () => clearInterval(interval)
  }, [])

  useEffect(() => {
    const timer = setInterval(() => {
      setStatusCheckTime(Date.now())
    }, 250)
    return () => clearInterval(timer)
  }, [])

  // 当前选中设备的遥测数据
  const currentTelemetry = selectedDeviceId
    ? telemetryMap[selectedDeviceId]
    : null

  const devicesWithRealtimeStatus = useMemo(() => {
    return devices.map((device) => {
      const parsedLastSeenAt = device.last_seen ? new Date(device.last_seen).getTime() : 0
      const lastSeenAt = Number.isFinite(parsedLastSeenAt) ? parsedLastSeenAt : 0
      const lastReceivedAt = telemetryReceivedAtMap[device.dev_id] || 0
      const lastActiveAt = Math.max(lastSeenAt, lastReceivedAt)
      const status = lastActiveAt > 0 && statusCheckTime - lastActiveAt <= DEVICE_OFFLINE_TIMEOUT_MS
        ? 'online'
        : 'offline'

      return {
        ...device,
        status,
      }
    })
  }, [devices, telemetryReceivedAtMap, statusCheckTime])

  // 当前选中设备的信息
  const currentDevice = devicesWithRealtimeStatus.find((d) => d.dev_id === selectedDeviceId)

  // 聚合所有终端的检测统计
  const totalDetectCount = useMemo(() => {
    const counts = { vis: 0, ir: 0, snd: 0, rad: 0 }
    for (const t of Object.values(telemetryMap)) {
      if (t.detect?.count) {
        counts.vis += t.detect.count.vis
        counts.ir += t.detect.count.ir
        counts.snd += t.detect.count.snd
        counts.rad += t.detect.count.rad
      }
    }
    return counts
  }, [telemetryMap])

  // 订阅所有设备的数据
  useEffect(() => {
    if (devices.length > 0) {
      const allDevIds = devices.map((d) => d.dev_id)
      subscribe(allDevIds)
    }
  }, [devices, subscribe])

  // SEI数据回调处理
  const handleSeiData = useCallback((data: any) => {
    if (!data || !data.dev_id) {
      return
    }

    const now = Date.now()
    setLastSeiTime(now)

    // 如果UDP数据超过2秒未更新，使用SEI数据
    if (now - lastUdpTime > 2000) {
      // 更新遥测数据
      updateTelemetry(data)
      console.log('[数据融合] 使用SEI数据 (UDP超时)')
    } else {
      // UDP数据新鲜，SEI数据作为备份
      console.log('[数据融合] SEI数据正常接收（使用UDP）')
    }
  }, [lastUdpTime, updateTelemetry])

  // 监控UDP数据更新
  useEffect(() => {
    if (currentTelemetry) {
      const now = Date.now()
      setLastUdpTime(now)
    }
  }, [currentTelemetry])

  // 默认选中设备：优先选择在线设备，否则选择第一个设备
  useEffect(() => {
    if (!selectedDeviceId && devicesWithRealtimeStatus.length > 0) {
      const onlineDevice = devicesWithRealtimeStatus.find((d) => d.status === 'online')
      if (onlineDevice) {
        selectDevice(onlineDevice.dev_id)
      } else {
        // 没有在线设备时，选择第一个设备（可能流仍然可用）
        selectDevice(devicesWithRealtimeStatus[0].dev_id)
      }
    }
  }, [devicesWithRealtimeStatus, selectedDeviceId, selectDevice])

  // 当 Modal 未打开时，把 player host 挂回到 Monitor 卡片内的 slot；同时切换控制栏显隐 class
  useLayoutEffect(() => {
    const host = visPlayerHostRef.current
    const slot = visCardSlotRef.current
    if (!host) return
    if (visModalVisible) {
      host.classList.add('host-in-modal')
    } else {
      host.classList.remove('host-in-modal')
      if (slot && host.parentNode !== slot) {
        slot.appendChild(host)
      }
    }
  }, [visModalVisible, selectedDeviceId])

  useLayoutEffect(() => {
    const host = irPlayerHostRef.current
    const slot = irCardSlotRef.current
    if (!host) return
    if (irModalVisible) {
      host.classList.add('host-in-modal')
    } else {
      host.classList.remove('host-in-modal')
      if (slot && host.parentNode !== slot) {
        slot.appendChild(host)
      }
    }
  }, [irModalVisible, selectedDeviceId])

  // 可见光视频刷新函数
  const refreshVisVideo = () => {
    setVisRefreshing(true)
    setVisVideoKey((prev) => prev + 1)
    setTimeout(() => {
      setVisRefreshing(false)
      message.success('可见光视频已刷新')
    }, 500)
  }

  // 红外视频刷新函数
  const refreshIrVideo = () => {
    setIrRefreshing(true)
    setIrVideoKey((prev) => prev + 1)
    setTimeout(() => {
      setIrRefreshing(false)
      message.success('红外视频已刷新')
    }, 500)
  }

  // 获取指定类型的视频流地址
  // 使用浏览器当前hostname替换后端返回URL中的host，
  // 确保在WSL等环境下浏览器能够直接访问ZLM
  const getStreamUrl = (type: 'vis' | 'ir') => {
    if (!currentDevice?.streams) {
      console.log('getStreamUrl: 当前设备或流信息为空')
      return ''
    }
    const url = currentDevice.streams[type] || ''
    if (!url) return ''
    try {
      const urlObj = new URL(url)
      urlObj.hostname = window.location.hostname
      const finalUrl = urlObj.toString()
      console.log(`getStreamUrl(${type}): ${finalUrl}`)
      return finalUrl
    } catch {
      console.log(`getStreamUrl(${type}): ${url}`)
      return url
    }
  }

  return (
    <div className="page-shell" style={{ padding: 0 }}>
      <Row gutter={12} style={{ height: '100%', margin: 0 }}>
        {/* 左侧：地图 + 视频 */}
        <Col span={16}>
          <Row gutter={[12, 12]} style={{ height: '100%' }}>
            {/* 地图区域 */}
            <Col span={24} style={{ height: '60%' }}>
              <Card
                title="目标态势图"
                size="small"
                styles={{ body: { padding: 0, height: 'calc(100% - 40px)' } }}
                style={{ height: '100%' }}
              >
                <MapContainer
                  telemetryMap={telemetryMap}
                  onlineDeviceIds={devicesWithRealtimeStatus
                    .filter((device) => device.status === 'online')
                    .map((device) => device.dev_id)}
                />
              </Card>
            </Col>

            {/* 视频区域 - 可见光和红外并排显示 */}
            <Col span={24} style={{ height: '40%' }}>
              <Row gutter={12} style={{ height: '100%' }}>
                {/* 可见光视频 */}
                <Col span={12} style={{ height: '100%' }}>
                  <Card
                    title={
                      <Space size={8}>
                        <span>可见光</span>
                        {currentDevice?.recording?.vis && (
                          <Tag color="red" style={{ marginInlineEnd: 0 }}>
                            录像中
                          </Tag>
                        )}
                      </Space>
                    }
                    size="small"
                    extra={
                      <Space size={4}>
                        {isAdmin() && <Button
                          type="text"
                          size="small"
                          icon={<ReloadOutlined spin={visRefreshing} />}
                          onClick={refreshVisVideo}
                          disabled={visRefreshing}
                          title="刷新视频流"
                        />}
                        {isAdmin() && <Button
                          type="text"
                          size="small"
                          icon={<AimOutlined />}
                          onClick={() => setVisModalVisible(true)}
                          title="检测控制"
                        />}
                      </Space>
                    }
                    styles={{ body: { padding: 0, height: 'calc(100% - 40px)' } }}
                    style={{ height: '100%' }}
                  >
                    <div ref={visCardSlotRef} style={{ width: '100%', height: '100%' }} />
                  </Card>
                </Col>
                {/* 红外视频 */}
                <Col span={12} style={{ height: '100%' }}>
                  <Card
                    title={
                      <Space size={8}>
                        <span>红外</span>
                        {currentDevice?.recording?.ir && (
                          <Tag color="red" style={{ marginInlineEnd: 0 }}>
                            录像中
                          </Tag>
                        )}
                      </Space>
                    }
                    size="small"
                    extra={
                      <Space size={4}>
                        <Button
                          type="text"
                          size="small"
                          icon={<ReloadOutlined spin={irRefreshing} />}
                          onClick={refreshIrVideo}
                          disabled={irRefreshing}
                          title="刷新视频流"
                        />
                        <Button
                          type="text"
                          size="small"
                          icon={<AimOutlined />}
                          onClick={() => setIrModalVisible(true)}
                          title="检测控制"
                        />
                      </Space>
                    }
                    styles={{ body: { padding: 0, height: 'calc(100% - 40px)' } }}
                    style={{ height: '100%' }}
                  >
                    <div ref={irCardSlotRef} style={{ width: '100%', height: '100%' }} />
                  </Card>
                </Col>
              </Row>
            </Col>
          </Row>
        </Col>

        {/* 右侧：状态面板 + 控制盘 */}
        <Col span={8} style={{ height: '100%' }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, height: '100%' }}>
            {/* 设备状态面板 - 占据大部分空间 */}
            <Card
              title="设备状态"
              size="small"
              extra={
                <Select
                  value={selectedDeviceId}
                  onChange={selectDevice}
                  style={{ width: 180 }}
                  placeholder="选择设备"
                  loading={loading}
                  options={devicesWithRealtimeStatus.map((d) => ({
                    value: d.dev_id,
                    label: (
                      <Space>
                        <span
                          className={`status-indicator ${d.status}`}
                        />
                        {d.name || d.dev_id}
                      </Space>
                    ),
                  }))}
                />
              }
              styles={{
                body: {
                  padding: '8px 14px',
                  height: 'calc(100% - 48px)',
                  overflow: 'auto'
                }
              }}
              style={{ flex: '1 1 auto', minHeight: 0 }}
            >
              <StatusPanel
                status={currentTelemetry?.status}
                devId={selectedDeviceId}
              />
            </Card>

            {/* 检测统计 - 固定高度 */}
            <Card
              title="检测统计"
              size="small"
              styles={{ body: { padding: '10px' } }}
              style={{ flex: '0 0 auto' }}
            >
              {Object.keys(telemetryMap).length > 0 ? (
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(2, 1fr)',
                  gap: '6px'
                }}>
                  <div className="detect-stat-card sensor-vis">
                    <div className="detect-stat-label">可见光</div>
                    <div className="detect-stat-value">{totalDetectCount.vis}</div>
                  </div>
                  <div className="detect-stat-card sensor-ir">
                    <div className="detect-stat-label">红外</div>
                    <div className="detect-stat-value">{totalDetectCount.ir}</div>
                  </div>
                  <div className="detect-stat-card sensor-snd">
                    <div className="detect-stat-label">声学</div>
                    <div className="detect-stat-value">{totalDetectCount.snd}</div>
                  </div>
                  <div className="detect-stat-card sensor-rad">
                    <div className="detect-stat-label">无线电</div>
                    <div className="detect-stat-value">{totalDetectCount.rad}</div>
                  </div>
                </div>
              ) : (
                <Text type="secondary">暂无数据</Text>
              )}
            </Card>
          </div>
        </Col>
      </Row>

      {/* 悬浮的云台控制按钮 */}
      <PtzController devId={selectedDeviceId} />

      {/* 通过 Portal 把播放器渲染到稳定的宿主 div，避免 Modal 打开时重新拉流 */}
      {selectedDeviceId && visPlayerHostRef.current && createPortal(
        <JessibucaPlayer
          key={`${selectedDeviceId}-vis-${visVideoKey}`}
          url={getStreamUrl('vis')}
          deviceId={selectedDeviceId}
          onSeiData={handleSeiData}
        />,
        visPlayerHostRef.current,
      )}
      {selectedDeviceId && irPlayerHostRef.current && createPortal(
        <JessibucaPlayer
          key={`${selectedDeviceId}-ir-${irVideoKey}`}
          url={getStreamUrl('ir')}
          deviceId={selectedDeviceId}
          onSeiData={handleSeiData}
        />,
        irPlayerHostRef.current,
      )}

      {/* 可见光检测控制Modal */}
      {isAdmin() && <VideoInteractionModal
        visible={visModalVisible}
        onClose={() => setVisModalVisible(false)}
        playerHost={visPlayerHostRef.current}
        deviceId={selectedDeviceId || ''}
        streamType="vis"
      />}

      {/* 红外检测控制Modal */}
      {isAdmin() && <VideoInteractionModal
        visible={irModalVisible}
        onClose={() => setIrModalVisible(false)}
        playerHost={irPlayerHostRef.current}
        deviceId={selectedDeviceId || ''}
        streamType="ir"
      />}
    </div>
  )
}

export default Monitor
